import {
  Accordion,
  Box,
  Button,
  ButtonsGroup,
  Card,
  Footer,
  Grid,
  Heading,
  InputPhone,
  InputSelect,
  InputText,
  Link,
  Maps,
  PageHeader,
  PageWrapper,
  Select,
  Typo
} from "#/atoms";
import { DESKTOP_SPACING, FONT_SIZE, NATURAL } from "@/theme";
import { FC, ReactNode, memo, useCallback, useState } from "react";
import { addProtocolIfMissing, countryList, generatePassword } from "@/helpers";
import { useInput, useInputSelect, useMaps, useSelect } from "@/hooks";

import { CustomerProps } from "./Customer.interface";
import { HeadingIcon } from "#/molecules";
import { InputHookProps } from "@/hooks/useInput";
import { InputType } from "@/components/atoms/Form/InputText/InputText.interface";

const Customer: FC<CustomerProps> = ({ onSave }) => {
  const { address, mapsAddress, onBlur } = useMaps();
  const [pwdType, setPwdType] = useState<InputType>('password');

  const companyName = useInput();
  const branchName = useInput();
  const email = useInput();
  const firstName = useInput();
  const lastName = useInput();
  const password = useInput();
  const passwordConfirm = useInput();
  const phone = useInput(); 
  const website = useInput(); 
  const notification = useInputSelect('notification');
  const terms = useInputSelect('terms', true);

  const vat = useInputSelect('vat');

  const customerCode = useInput();
  const houseName = useInput();
  const houseNumber = useInput();
  const road = useInput();
  const area = useInput();
  const town = useInput();
  const county = useInput();
  const postcode = useInput();
  const countryId = useSelect('1');
  const active = useInputSelect('active');
  const payByCard = useInputSelect('payByCard');
  const useLowQualityThumbnails = useInputSelect('useLowQualityThumbnails');
  const scrubData = useInputSelect('scrubData');

  const contactName = useInput();
  const contactEmail = useInput();
  const contactPhone1 = useInput();
  const contactPhone2 = useInput();

  const validate = useCallback(() => {
    let isValid = true;

    if (!companyName.value) {
      isValid = false;
      companyName.setError('Name is incorrect');
    } else {
      companyName.setError(null);
    }

    if (!customerCode.value) {
      isValid = false;
      customerCode.setError('Customer code is empty');
    } else {
      customerCode.setError(null);
    }

    if (!firstName.value) {
      isValid = false;
      firstName.setError('First name is missing.');
    } else {
      firstName.setError(null);
    }

    if (!lastName.value) {
      isValid = false;
      lastName.setError('Last name is missing.');
    } else {
      lastName.setError(null);
    }

    if (!email.value || !email.value.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
      isValid = false;
      email.setError('Email is incorrect.');
    } else {
      email.setError(null);
    }

    if (password.value?.length < 6) {
      isValid = false;
      password.setError('Password is too short.');
    } else {
      password.setError(null);
    }

    if (passwordConfirm.value?.length < 6) {
      isValid = false;
      passwordConfirm.setError('Password is too short.');
    } else {
      passwordConfirm.setError(null);
    }

    if (password.value !== passwordConfirm.value) {
      isValid = false;
      passwordConfirm.setError('Passwords do not match.');
    } else {
      passwordConfirm.setError(null);
    }

    return isValid;
  }, [companyName, customerCode, firstName, lastName, email, password, passwordConfirm]);

  const onChangeValue = (e : React.ChangeEvent<HTMLInputElement>, input: InputHookProps) => {
    input.onChange(e);
  }

  const handleGenerate = () => {
      setPwdType('text');
      const pwd = generatePassword(12);
      password.setValue(pwd);
      passwordConfirm.setValue(pwd);
  };

  const handleSave = useCallback(async () => {
    const isValid = validate(); 

    if (!isValid) {
      return;
    }

    await onSave(
      {
        active: active.checked,
        branchName: branchName.value,
        code: customerCode.value,
        companyName: companyName.value,
        confirmPassword: passwordConfirm.value,
        email: email.value,
        firstName: firstName.value,
        invited: false,
        lastName: lastName.value,
        password: password.value,
        phone: phone.value,
        website: website.value
      },
      {
        area: area.value,
        countryId: parseInt(countryId.value, 10),
        county: county.value,
        houseName: houseName.value,
        houseNumber: houseNumber.value,
        isCardPayment: payByCard.checked,
        isLowQuality: useLowQualityThumbnails.checked,
        postcode: postcode.value,
        road: road.value,
        shouldRemoveData: scrubData.checked,
        town: town.value,
        vat: vat.checked
      },
      {
        contactId: '',
        customerId: '',
        email: contactEmail.value,
        name: contactName.value,
        phone1: contactPhone1.value,
        phone2: contactPhone2.value,
        sendInvoice: false,
        type: 0,
      },
      notification.checked
    );
  }, [
    active,
    address,
    area,
    branchName,
    customerCode,
    companyName,
    contactEmail,
    contactName,
    contactPhone1,
    contactPhone2,
    countryId,
    county,
    email,
    firstName,
    houseName,
    houseNumber,
    lastName,
    notification,
    password,
    passwordConfirm,
    payByCard,
    postcode,
    road,
    scrubData,
    terms,
    town,
    useLowQualityThumbnails,
    vat,
    website
  ]);

  const labelLink : ReactNode = <>Agree to&nbsp;<a href="https://www.photoplan.co.uk/terms/" target="_blank" rel="noreferrer">Terms & Conditions</a></>;
  
  return (
    <>
      <PageWrapper pb="100px">
        <PageHeader>
          <Heading scale="h1" fontColor={NATURAL.K}>Create New Customer</Heading>
          <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM}>Please provide us information to create new customer.</Typo>
        </PageHeader>

        <Card padding="0">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="contacts" title="Account Details" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
              <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(3, 30%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
                <InputText type="text" label="Company Name" value={companyName.value} onChange={(e) => onChangeValue(e, companyName)} errorMessage={companyName.error || ''} state={companyName.error ? 'error' : 'idle'} />
                <InputText type="text" label="Customer Code" value={customerCode.value} onChange={(e) => onChangeValue(e, customerCode)} errorMessage={customerCode.error || ''} state={customerCode.error ? 'error' : 'idle'} />
                <InputText type="text" label="Branch Name" value={branchName.value} onChange={branchName.onChange} />
                <InputText type="text" label="First Name" value={firstName.value} onChange={(e) => onChangeValue(e, firstName)} errorMessage={firstName.error || ''} state={firstName.error ? 'error' : 'idle'} />
                <InputText type="text" label="Last Name" value={lastName.value} onChange={(e) => onChangeValue(e, lastName)} errorMessage={lastName.error || ''} state={lastName.error ? 'error' : 'idle'} />
                <InputText type="email" label="E-mail Address" value={email.value} onChange={(e) => onChangeValue(e, email)} errorMessage={email.error || ''} state={email.error ? 'error' : 'idle'} />
                <InputText type={pwdType} label="Password" value={password.value} onChange={(e) => onChangeValue(e, password)}  errorMessage={password.error || ''} state={password.error ? 'error' : 'idle'}/>
                <InputText type={pwdType} label="Confirm Password" value={passwordConfirm.value} onChange={(e) => onChangeValue(e, passwordConfirm)} errorMessage={passwordConfirm.error || ''} state={passwordConfirm.error ? 'error' : 'idle'} />
                <InputPhone type="tel" label="Phone" value={phone.value} onChange={phone.onChange} />
                <Grid rowGap={DESKTOP_SPACING.XL} gridTemplateRows="auto" margin={`${DESKTOP_SPACING.SM}px 0 0`}>
                  <InputSelect type="checkbox" checked={vat.checked} label="Customer VAT" value={vat.value} onChangeValue={vat.onChange} />
                  <InputSelect
                    type="checkbox"
                    checked={notification.checked}
                    label="Send E-mail Notification"
                    value={notification.value}
                    onChangeValue={notification.onChange}
                  />
                  <InputSelect
                    type="checkbox"
                    checked={terms.checked}
                    label={labelLink}
                    value={terms.value}
                    onChangeValue={terms.onChange}
                  />
                </Grid>
                <Box>
                  <InputText type="url" label="Website" value={website.value} onChange={website.onChange} />
                  <Link href={addProtocolIfMissing(website.value)} target="_blank">{website.value}</Link>
                </Box>
                <ButtonsGroup>
                  <Button onClick={handleGenerate}>Generate password</Button>
                </ButtonsGroup>
              </Grid>
              
            </Accordion>
          </Box>
        </Card>

        <Card padding="0">
          <Grid gridTemplateColumns="repeat(2, 1fr)">
            <Box padding={`${DESKTOP_SPACING.EL}px`}>
              <HeadingIcon
                icon="location"
                title="Address Details"
              />
              <InputText type="text" label="House Name" value={houseName.value} onChange={houseName.onChange} />
              <InputText type="text" label="Number" value={houseNumber.value} onChange={houseNumber.onChange} />
              <InputText type="text" label="Road" value={road.value} onChange={road.onChange} />
              <InputText type="text" label="Area" value={area.value} onChange={area.onChange} />
              <InputText type="text" label="Town" value={town.value} onChange={town.onChange} />
              <InputText type="text" label="County" value={county.value} onChange={county.onChange} />
              <InputText type="text" label="Postcode" value={postcode.value} onBlur={onBlur} onChange={postcode.onChange} />
              <Select label="Country"
                  options={Object.keys(countryList).map((k) => { return { id: k.replace("C", ""), label: countryList[k].name} })}
                  onChange={countryId.onChange}
                  selected={countryId.value}
                />
              <InputSelect type="checkbox" checked={active.checked} label="Is Active" value={active.value} onChangeValue={active.onChange} />
              <InputSelect type="checkbox" checked={payByCard.checked} label="Pay by Card" value={payByCard.value} onChangeValue={payByCard.onChange} />
              <InputSelect type="checkbox" checked={useLowQualityThumbnails.checked} label="Use low quality thumbnails" value={useLowQualityThumbnails.value} onChangeValue={useLowQualityThumbnails.onChange} />
              <InputSelect type="checkbox" checked={scrubData.checked} label="Scrub personal data once order completes" value={scrubData.value} onChangeValue={scrubData.onChange} />
            </Box>
            <Box>
              <Maps address={mapsAddress} />
            </Box>
          </Grid>
        </Card>

        <Card padding="0">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="contacts" title="Contact Details" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
              <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 40%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
                <InputText type="text" label="Contact Name" value={contactName.value} onChange={contactName.onChange} />
                <InputText type="text" label="E-mail Address" value={contactEmail.value} onChange={contactEmail.onChange} />
                <InputPhone type="tel" label="Phone Number 1" value={contactPhone1.value} onChange={contactPhone1.onChange} />
                <InputPhone type="tel" label="Phone Number 2" value={contactPhone2.value} onChange={contactPhone2.onChange} />
              </Grid>
            </Accordion>
          </Box>
        </Card>
      </PageWrapper>
      <Footer>
        <Button onClick={handleSave}>Save</Button>
      </Footer>
    </>
  );
};

export default memo(Customer);
